import { supabase } from '../lib/supabase';
import type { Product } from '../hooks/useProductSystem';
import { formatOpenAIResponse } from './textFormatting';

// Cache pour stocker les produits récupérés par chatbot
const productCache: Record<string, { products: Product[], timestamp: number }> = {};
// Durée de validité du cache en millisecondes (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Détecte les mentions de produits dans un texte et récupère les informations des produits mentionnés
 * @param text Le texte dans lequel chercher des mentions de produits
 * @param chatbotPublicId L'identifiant du chatbot
 * @returns Un tableau d'objets de produits mentionnés
 */
export async function detectProductMentions(
  text: string, 
  chatbotPublicId: string
): Promise<Product[]> {
  if (!text || !chatbotPublicId) return [];
  
  try {
    // Vérifier le cache d'abord
    const cacheKey = chatbotPublicId;
    const now = Date.now();
    let allProducts: Product[] = [];
    
    // Utiliser le cache si disponible et valide
    if (productCache[cacheKey] && (now - productCache[cacheKey].timestamp) < CACHE_TTL) {
      allProducts = productCache[cacheKey].products;
    } else {
      // Récupérer tous les produits de ce chatbot
      const { data: products, error } = await supabase
        .from('products')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId);
      
      if (error || !products || products.length === 0) {
        return [];
      }
      
      // Convertir les produits au format Product
      allProducts = products.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description || undefined,
        price: p.price || 0,
        currency: p.currency || 'EUR',
        image_url: p.image_url || undefined,
        category: p.category || undefined,
        tags: p.tags || [],
        sku: p.sku || undefined,
        stock: p.stock || 0,
        position: p.position || 0,
        source: (p.source || 'manual') as 'manual' | 'csv' | 'shopify' | 'prestashop',
        metadata: p.metadata as Record<string, unknown> || {},
        discount: p.discount || 0
      }));
      
      // Mettre en cache les produits
      productCache[cacheKey] = {
        products: allProducts,
        timestamp: now
      };
    }
    
    // Créer un ensemble de produits mentionnés pour éviter les doublons
    const mentionedProductsSet = new Set<string>();
    const mentionedProducts: Product[] = [];
    
    // Normaliser le texte pour la recherche
    const normalizedText = ' ' + text.toLowerCase() + ' ';
    
    // Optimisation: pré-traiter les noms de produits une seule fois
    const productNameMap = allProducts.map(product => ({
      product,
      normalizedName: product.name.toLowerCase(),
      regex: new RegExp(`\\b${product.name.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i')
    }));
    
    // Utiliser un traitement parallèle pour les grandes listes de produits
    if (productNameMap.length > 100) {
      // Diviser la liste en chunks pour le traitement parallèle
      const chunkSize = 50;
      const chunks = [];
      
      for (let i = 0; i < productNameMap.length; i += chunkSize) {
        chunks.push(productNameMap.slice(i, i + chunkSize));
      }
      
      // Traiter chaque chunk en parallèle
      const chunkResults = await Promise.all(
        chunks.map(chunk => 
          new Promise<Product[]>(resolve => {
            const found: Product[] = [];
            
            for (const { product, regex } of chunk) {
              if (regex.test(normalizedText) && !mentionedProductsSet.has(product.id)) {
                mentionedProductsSet.add(product.id);
                found.push(product);
              }
            }
            
            resolve(found);
          })
        )
      );
      
      // Fusionner les résultats
      chunkResults.forEach(results => {
        mentionedProducts.push(...results);
      });
    } else {
      // Pour les petites listes, traitement séquentiel standard
      for (const { product, regex } of productNameMap) {
        if (regex.test(normalizedText) && !mentionedProductsSet.has(product.id)) {
          mentionedProductsSet.add(product.id);
          mentionedProducts.push(product);
        }
      }
    }
    
    return mentionedProducts;
  } catch (err) {
    return [];
  }
}

/**
 * Crée une version simplifiée d'un composant de vignette produit en HTML
 * pour l'injection dans un message de chatbot
 * @param product Le produit à afficher
 * @param index L'index du produit dans la liste (pour les animations progressives)
 * @param settings Les paramètres de settings pour le produit
 * @returns Une chaîne HTML représentant la vignette du produit
 */
export function createProductCard(product: Product, index: number = 0, settings?: { showPrices?: boolean, showStock?: boolean }): string {
  if (!product) return '';
  
  // Utiliser les paramètres de settings passés en paramètre - FORCER showPrices à true par défaut
  const showPrices = settings?.showPrices !== false; // Afficher les prix sauf si explicitement false
  const showStock = settings?.showStock === true;
  
  // Récupérer les couleurs de gradient depuis les variables CSS
  const gradientStart = 'var(--gradient-start, #7C3AED)';
  const gradientEnd = 'var(--gradient-end, #2DD4BF)';
  
  // Formater le prix (avec réduction si applicable)
  const formatPrice = (price: number, discount?: number) => {
    if (!price) return '';
    
    if (!discount) {
      return `${price.toFixed(2)} ${product.currency}`;
    }
    
    const discountedPrice = price * (1 - discount / 100);
    return `${discountedPrice.toFixed(2)} ${product.currency} <span style="font-size: 0.75rem; text-decoration: line-through; opacity: 0.7; color: #666; margin-left: 0.5rem;">${price.toFixed(2)} ${product.currency}</span>`;
  };
  
  // Utiliser des styles simplifiés mais efficaces
  const isRedProduct = product.name.toLowerCase().includes('rouge');
  const bgColor = isRedProduct ? '#fff0f0' : '#ffffff';
  
  // Animation légère, plus rapide à afficher
  const animationStyle = `animation: fadeIn 0.3s ease forwards; animation-delay: ${index * 0.1}s; opacity: 0;`;
  
  // Optimisation pour le chargement des images en priorité
  const imageLoading = index < 2 ? 'eager' : 'lazy'; // Charger les deux premières images immédiatement
  
  return `
    <div style="position: relative; border-radius: 0.5rem; overflow: hidden; margin-bottom: 0.75rem; border: 2px solid ${gradientStart}; background-color: ${bgColor}; ${animationStyle}" data-product-id="${product.id}" class="product-card" onclick="openProductModal('${product.id}')">
      <div style="padding: 1rem; position: relative;">
        <div style="display: flex; gap: 1rem; align-items: flex-start;">
          ${product.image_url ? `
            <div style="position: relative; flex-shrink: 0; width: 7rem; height: 7rem; border-radius: 0.375rem; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.1);">
              <img
                src="${product.image_url}"
                alt="${product.name}"
                style="width: 100%; height: 100%; object-fit: cover;"
                loading="${imageLoading}"
                fetchpriority="high"
              />
              ${product.discount ? `
                <div style="position: absolute; top: 0; left: 0; width: 100%; background-image: linear-gradient(to right, ${gradientStart}, ${gradientEnd}); color: white; font-weight: 600; padding: 0.25rem 0.75rem; font-size: 0.875rem; text-align: center;">
                  -${product.discount}%
                </div>
              ` : ''}
            </div>
          ` : ''}
          
          <div style="flex: 1; display: flex; flex-direction: column; justify-content: space-between; height: 7rem; overflow: hidden;">
            <div>
              <h4 style="font-weight: 700; font-size: 1rem; background-image: linear-gradient(to right, ${gradientStart}, ${gradientEnd}); -webkit-background-clip: text; background-clip: text; color: transparent; margin: 0; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">
                ${product.name}
              </h4>
              
              ${product.description ? `
                <p style="font-size: 0.875rem; color: #374151; margin: 0.25rem 0; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">
                  ${product.description}
                </p>
              ` : ''}
            </div>
            
            <div style="display: flex; align-items: baseline; gap: 0.75rem; margin-top: auto;">
              ${product.price && showPrices ? `
                <div style="font-size: 1rem; font-weight: 600; background-image: linear-gradient(to right, ${gradientStart}, ${gradientEnd}); -webkit-background-clip: text; background-clip: text; color: transparent;">
                  ${formatPrice(product.price, product.discount)}
                </div>
              ` : ''}
              
              ${product.stock !== undefined && showStock ? `
                <span style="font-size: 0.75rem; padding: 0.125rem 0.625rem; border-radius: 9999px; background-color: ${gradientStart}10; border: 1px solid ${gradientStart}20; color: ${gradientStart}; font-weight: 500; display: inline-block;">
                  Stock: ${product.stock}
                </span>
              ` : ''}
            </div>
          </div>
        </div>
        
        <button 
          style="position: absolute; bottom: 0.75rem; right: 0.75rem; padding: 0.5rem; border-radius: 9999px; background-image: linear-gradient(to right, ${gradientStart}, ${gradientEnd}); color: white; cursor: pointer; border: none; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 3px rgba(0,0,0,0.2);"
          data-product-id="${product.id}"
          onclick="openProductModal('${product.id}'); event.stopPropagation();"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </button>
      </div>
    </div>
  `;
}

// Modifier la fonction formatOpenAIResponseWithProducts pour utiliser un nouveau format plus simple
export async function formatOpenAIResponseWithProducts(
  text: string, 
  chatbotPublicId: string,
  mentionedProducts?: Product[]
): Promise<string> {
  // Formatter d'abord le texte avec la fonction standard
  const formattedText = formatOpenAIResponse(text);
  
  // Si pas de chatbotPublicId, retourner simplement le texte formaté
  if (!chatbotPublicId) return formattedText;
  
  try {
    // Utiliser les produits fournis ou détecter les produits mentionnés
    const products = mentionedProducts || await detectProductMentions(text, chatbotPublicId);
    
    // Si aucun produit n'est mentionné, retourner simplement le texte formaté
    if (!products || products.length === 0) return formattedText;
    
    // Récupérer les couleurs de gradient depuis les variables CSS
    const gradientStart = 'var(--gradient-start, #7C3AED)';
    const gradientEnd = 'var(--gradient-end, #2DD4BF)';
    
    // Créer un élément HTML élégant pour "View Products"
    const viewProductsButton = `
      <div style="margin-top: 0.75rem; margin-bottom: 0.5rem; text-align: right; opacity: 0; animation: fadeIn 0.5s ease forwards; animation-delay: 0.5s;">
        <a 
          href="javascript:void(0)" 
          class="products-button"
          data-product-ids="${products.map(p => p.id).join(',')}"
          style="display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.125rem 0.375rem; border: 1px solid rgba(124, 58, 237, 0.5); border-radius: 9999px; cursor: pointer; font-weight: 400; transition: all 0.2s ease; background: rgba(124, 58, 237, 0.2); color: rgba(124, 58, 237, 0.95); font-size: 0.7rem; opacity: 0.8; box-shadow: 0 1px 2px rgba(0,0,0,0.05); text-decoration: none;"
          onmouseover="this.style.opacity='1'; this.style.background='rgba(124, 58, 237, 0.3)'; this.style.transform='scale(1.02)'"
          onmouseout="this.style.opacity='0.8'; this.style.background='rgba(124, 58, 237, 0.2)'; this.style.transform='scale(1)'"
          title="Cliquez pour voir les produits mentionnés"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
          </svg>
          <span>${products.length} produit${products.length > 1 ? 's' : ''}</span>
        </a>
      </div>
      <style>
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      </style>
      <script>
        // Ajout de l'écouteur d'événements au bouton directement
        document.addEventListener('DOMContentLoaded', function() {
          // Trouver tous les boutons de produits
          const productButtons = document.querySelectorAll('.products-button');
          
          // Ajouter l'écouteur d'événements à chaque bouton
          productButtons.forEach(button => {
            button.addEventListener('click', function(event) {
              event.preventDefault();
              
              // Récupérer les IDs des produits
              const productIdsStr = this.getAttribute('data-product-ids') || '';
              let productIds = productIdsStr.split(',').filter(id => id.trim().length > 0);
              
              if (productIds.length > 0) {
                console.log('Bouton produit cliqué, IDs bruts:', productIdsStr);
                console.log('Bouton produit cliqué, IDs traités:', productIds);
                
                // Afficher un élément de débogage
                const debugElement = document.createElement('div');
                debugElement.textContent = 'IDs produits: ' + productIds.join(', ');
                debugElement.style.position = 'fixed';
                debugElement.style.top = '10px';
                debugElement.style.right = '10px';
                debugElement.style.background = 'rgba(0,0,0,0.8)';
                debugElement.style.color = 'white';
                debugElement.style.padding = '10px';
                debugElement.style.borderRadius = '5px';
                debugElement.style.zIndex = '999999';
                debugElement.style.fontSize = '12px';
                document.body.appendChild(debugElement);
                setTimeout(() => debugElement.remove(), 5000);
                
                // Créer et dispatcher l'événement d'ouverture de mini-app
                window.dispatchEvent(new CustomEvent('open-products-miniapp', {
                  detail: { productIds: productIds }
                }));
                
                console.log('Événement open-products-miniapp envoyé directement à window');
                
                // Si l'approche précédente ne fonctionne pas, essayons de déclencher l'événement globalement
                if (typeof window.useMiniAppModal !== 'undefined') {
                  try {
                    console.log('Tentative d\'appel direct à useMiniAppModal.getState().setActiveApp');
                    window.useMiniAppModal.getState().setActiveApp('products', {
                      products: {
                        mentionedIds: productIds
                      }
                    });
                  } catch (err) {
                    console.error('Erreur lors de l\'appel direct à useMiniAppModal:', err);
                  }
                }
              }
            });
          });
        });
      </script>
    `;
    
    // Retourner le texte formaté suivi du bouton pour voir les produits
    return formattedText + viewProductsButton;
  } catch (err) {
    console.error('Error in formatOpenAIResponseWithProducts:', err);
    return formattedText;
  }
} 